import { useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { Box, Text } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { useSidebarContext } from '@/hooks/use-sidebar-context'

const INDICATOR_WIDTH = 14

const ScoreEnd = styled.span`
  display: flex;
  justify-content: center;
  font-size: 12px;
  color: #5a6881;
  font-weight: 500;
  text-wrap: nowrap;
`

const Indicator = styled.div`
  position: relative;
  width: ${INDICATOR_WIDTH}px;
  height: 28px;
  bottom: 7px;
  border: ${props => `3px solid ${props.color}`};
  filter: drop-shadow(0px 0px 1px rgba(0, 0, 0, 0.25)) drop-shadow(0px 2px 1px rgba(0, 0, 0, 0.05));
  background-color: #fff;
  border-radius: 20px;
`

const StandaloneRiskBar = ({ score = 0, isLocked }) => {
  const [width, setWidth] = useState(0)
  const { isSidebarOpen } = useSidebarContext()
  const ref = useRef(null)

  const handleResize = () => {
    setTimeout(() => {
      if (ref.current) {
        const newWidth = ref.current.getBoundingClientRect().width
        setWidth(newWidth)
      }
    }, 150) // Delay execution to ensure transitions have completed
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    handleResize()
  }, [isSidebarOpen])

  useEffect(() => {
    setTimeout(() => {
      handleResize()
    }, 2000)
  }, [])

  const getIndicatorColor = score => {
    if (score < 30) {
      return vars.backgroundColor.positive
    } else if (score < 50) {
      return vars.backgroundColor.caution
    } else {
      return vars.backgroundColor.critical
    }
  }

  return (
    <Box
      width="full"
      display="flex"
      paddingBottom="xsmall"
      position="relative"
      flexDirection="column"
      ref={ref}
      style={{ maxWidth: '700px' }}
      gap="xxsmall"
    >
      <Box width="full" display="flex" alignItems="center" justifyContent="spaceBetween">
        <ScoreEnd>Low risk</ScoreEnd>
        <ScoreEnd>High risk</ScoreEnd>
      </Box>

      <Box
        background="brandAccentSoft"
        display="flex"
        borderRadius="xlarge"
        overflow="hidden"
        gap="xxxsmall"
      >
        <motion.div
          style={{ position: 'absolute', zIndex: '2' }}
          initial={{ x: 0 }}
          whileInView={{ x: `${(score / 100) * width - INDICATOR_WIDTH / 2}px` }}
          transition={{ delay: 0, type: 'spring', damping: 20, mass: 1 }}
          viewport={{ once: true }}
        >
          {!isLocked && <Indicator color={getIndicatorColor(score)} />}
        </motion.div>
        <Box
          background="white"
          style={{
            width: `${100 - score}%`,
            height: '15px',
            position: 'absolute',
            opacity: '0.6',
            left: `${score}%`
          }}
        />

        <Box background="positive" style={{ width: '30%', height: '15px' }} />
        <Box background="caution" style={{ width: '20%', height: '15px' }} />
        <Box background="critical" style={{ width: '50%', height: '15px' }} />
      </Box>

      <Box width="full" paddingTop="xxsmall">
        <Box position="absolute" display="flex" justifyContent="flexEnd">
          <Text size="xsmall" tone="tertiary">
            0%
          </Text>
        </Box>
        <Box style={{ width: `33%` }} position="absolute" display="flex" justifyContent="flexEnd">
          <Text size="xsmall" tone="tertiary">
            30%
          </Text>
        </Box>
        <Box style={{ width: `54%` }} position="absolute" display="flex" justifyContent="flexEnd">
          <Text size="xsmall" tone="tertiary">
            50%
          </Text>
        </Box>
        <Box style={{ width: `100%` }} position="absolute" display="flex" justifyContent="flexEnd">
          <Text size="xsmall" tone="tertiary">
            100%
          </Text>
        </Box>
      </Box>

      {/* <motion.svg width={'105%'} height="60" initial="visible">
        <motion.line
          x1="8"
          x2={'30%'}
          y1="30"
          y2="30"
          stroke="#12784F"
          strokeWidth={16}
          strokeLinecap="round"
          variants={draw}
        />
        <motion.line
          x1={'50%'}
          y1="30"
          x2={'95%'}
          y2="30"
          stroke="#DC2C2D"
          strokeWidth={16}
          strokeLinecap="round"
          variants={draw}
          custom={1.5}
        />
        <motion.line
          x1={'30%'}
          y1="30"
          x2={'50%'}
          y2="30"
          stroke="#FDD651"
          strokeWidth={16.5}
          variants={draw}
          custom={1}
        />
      </motion.svg> */}
    </Box>
  )
}

export default StandaloneRiskBar
